import { useState, useEffect } from "react";
import axios from "axios";
import { DashboardLayout, workerNavItems, teamLeaderNavItems } from "@/components/DashboardLayout";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { 
  DollarSign, 
  TrendingUp,
  Percent,
  Calendar,
  Building2,
  Wallet,
  PiggyBank
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function WorkerEarnings() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [earningsData, setEarningsData] = useState(null);

  useEffect(() => {
    fetchMyEarnings();
  }, []);

  const fetchMyEarnings = async () => {
    try {
      const response = await axios.get(`${API}/earnings/my`);
      setEarningsData(response.data);
    } catch (error) {
      console.error("Error fetching earnings:", error);
      toast.error(t('earnings.unableToLoad'));
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('nb-NO', { 
      style: 'currency', 
      currency: 'NOK',
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  // Calculate commission rate
  const commissionRate = earningsData?.summary?.commission_rate || 2;

  // Find max month for progress bar scaling
  const maxMonthlyEarning = earningsData?.monthly_breakdown?.length > 0
    ? Math.max(...earningsData.monthly_breakdown.map(m => m.gross), 1)
    : 1;

  // Format month name from YYYY-MM
  const formatMonthName = (monthStr) => {
    if (!monthStr) return '';
    const [year, month] = monthStr.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1, 1);
    return date.toLocaleDateString('nb-NO', { month: 'short' });
  };

  // Determine nav items based on user role
  const { user } = useAuth();
  const navItems = user?.role === 'team_leader' ? teamLeaderNavItems : workerNavItems;

  return (
    <DashboardLayout navItems={navItems} title={t('earnings.myEarnings')}>
      <div className="space-y-6" data-testid="worker-earnings-page">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-10 h-10 border-2 border-[#23F7DD] border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : earningsData ? (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card className="bg-[#0A0A0A] border-white/10">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-green-500/10 rounded-lg">
                      <DollarSign className="w-5 h-5 text-green-400" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-green-400">
                        {formatCurrency(earningsData.summary?.gross_earnings)}
                      </p>
                      <p className="text-xs text-[#94A3B8]">{t('earnings.totalGrossEarnings')}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-[#0A0A0A] border-white/10">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-purple-500/10 rounded-lg">
                      <PiggyBank className="w-5 h-5 text-purple-400" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-purple-400">
                        {formatCurrency(earningsData.summary?.platform_commission)}
                      </p>
                      <p className="text-xs text-[#94A3B8]">{t('earnings.platformFee')} ({earningsData.summary?.commission_rate || 2}%)</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-[#0A0A0A] border-white/10">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-[#23F7DD]/10 rounded-lg">
                      <Wallet className="w-5 h-5 text-[#23F7DD]" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-[#23F7DD]">
                        {formatCurrency(earningsData.summary?.net_earnings)}
                      </p>
                      <p className="text-xs text-[#94A3B8]">{t('earnings.netEarnings')}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-[#0A0A0A] border-white/10">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-yellow-500/10 rounded-lg">
                      <Building2 className="w-5 h-5 text-yellow-400" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-yellow-400">
                        {earningsData.summary?.projects_count || 0}
                      </p>
                      <p className="text-xs text-[#94A3B8]">{t('earnings.completedProjects')}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Monthly Chart */}
            <Card className="bg-[#0A0A0A] border-white/10">
              <CardHeader>
                <CardTitle className="text-white font-['Unbounded'] flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-[#23F7DD]" />
                  {t('earnings.monthlyEarnings')} ({new Date().getFullYear()})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {(earningsData.monthly_breakdown || []).map((month) => {
                    const monthNet = month.gross * (1 - commissionRate / 100);
                    return (
                      <div key={month.month} className="flex items-center gap-4">
                        <span className="text-[#94A3B8] w-16 text-sm">{formatMonthName(month.month)}</span>
                        <div className="flex-1">
                          <Progress 
                            value={(month.gross / maxMonthlyEarning) * 100} 
                            className="h-6"
                          />
                        </div>
                        <div className="text-right w-36">
                          {month.gross > 0 ? (
                            <>
                              <p className="text-[#23F7DD] font-medium">{formatCurrency(monthNet)}</p>
                              <p className="text-xs text-[#94A3B8]">{t('earnings.gross')}: {formatCurrency(month.gross)}</p>
                            </>
                          ) : (
                            <p className="text-[#475569]">-</p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                  {(!earningsData.monthly_breakdown || earningsData.monthly_breakdown.length === 0) && (
                    <p className="text-[#94A3B8] text-center py-4">{t('earnings.noEarningsYet')}</p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Projects List */}
            <Card className="bg-[#0A0A0A] border-white/10">
              <CardHeader>
                <CardTitle className="text-white font-['Unbounded'] flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-[#23F7DD]" />
                  {t('earnings.completedProjects')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {(!earningsData.projects || earningsData.projects.length === 0) ? (
                  <div className="text-center py-12">
                    <DollarSign className="w-12 h-12 text-[#475569] mx-auto mb-4" />
                    <p className="text-[#94A3B8]">{t('earnings.noCompletedProjects')}</p>
                    <p className="text-sm text-[#475569] mt-2">
                      {t('earnings.earningsAppearHere')}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {earningsData.projects.map((project, idx) => {
                      const grossAmt = project.gross_amount || 0;
                      const netAmt = grossAmt * (1 - commissionRate / 100);
                      const feeAmt = grossAmt * (commissionRate / 100);
                      
                      return (
                        <div 
                          key={idx} 
                          className="p-4 bg-white/5 rounded-lg border border-white/10"
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <h4 className="text-white font-medium">{project.project_title}</h4>
                              {project.role && (
                                <p className="text-sm text-[#23F7DD]">{project.role}</p>
                              )}
                              <div className="flex items-center gap-4 mt-2 text-xs text-[#94A3B8]">
                                {project.percentage > 0 && (
                                  <span className="flex items-center gap-1">
                                    <Percent className="w-3 h-3" />
                                    {project.percentage}% {t('earnings.share')}
                                  </span>
                                )}
                                {project.team_total && (
                                  <span className="flex items-center gap-1">
                                    Team: {formatCurrency(project.team_total)}
                                  </span>
                                )}
                                <span className="flex items-center gap-1">
                                  <Calendar className="w-3 h-3" />
                                  {project.date && new Date(project.date).toLocaleDateString('nb-NO')}
                                </span>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="text-[#23F7DD] font-bold text-lg">
                                {formatCurrency(netAmt)}
                              </p>
                              <p className="text-xs text-[#94A3B8]">
                                {t('earnings.gross')}: {formatCurrency(grossAmt)}
                              </p>
                              <p className="text-xs text-purple-400">
                                {t('earnings.fee')}: {formatCurrency(feeAmt)}
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Commission Info */}
            <Card className="bg-purple-500/10 border-purple-500/30">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <Percent className="w-5 h-5 text-purple-400 mt-0.5" />
                  <div>
                    <h4 className="text-white font-medium">{t('earnings.platformCommission')}</h4>
                    <p className="text-sm text-[#94A3B8] mt-1">
                      {t('earnings.commissionInfo', { rate: earningsData?.summary?.commission_rate || 2 })}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </>
        ) : (
          <div className="text-center py-12">
            <DollarSign className="w-12 h-12 text-[#475569] mx-auto mb-4" />
            <p className="text-[#94A3B8]">{t('earnings.unableToLoad')}</p>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
